import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Download,
  Info,
  FolderOpen
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const FileList = ({ items, onItemClick, onDownload, onGetInfo, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const formatFileSize = (size) => {
    if (!size) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let fileSize = size;
    
    while (fileSize >= 1024 && unitIndex < units.length - 1) {
      fileSize /= 1024;
      unitIndex++;
    }
    
    return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'dmp': return '🔧';
      case 'exe': return '⚙️';
      case 'dll': return '📚';
      case 'pdb': return '🔍';
      case 'txt': case 'log': case 'md': return '📄';
      case 'bat': return '⚡';
      case 'html': return '🌐';
      case 'zip': case 'rar': case '7z': return '📦';
      case 'pdf': return '📕';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return '🖼️';
      default: return '📄';
    }
  };

  const getFileTypeColor = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'dmp': return 'error';
      case 'exe': return 'warning';
      case 'dll': case 'pdb': return 'info';
      case 'txt': case 'log': case 'md': return 'default';
      case 'bat': return 'secondary';
      case 'html': return 'primary';
      case 'zip': case 'rar': case '7z': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          파일 목록을 불러오는 중...
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          이 폴더는 비어있습니다
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          파일이나 하위 폴더가 없습니다.
        </Typography>
      </Box>
    );
  }

  // 폴더를 먼저, 파일을 나중에 정렬
  const sortedItems = [...items].sort((a, b) => {
    if (a.type === 'folder' && b.type === 'file') return -1;
    if (a.type === 'file' && b.type === 'folder') return 1;
    return a.name.localeCompare(b.name);
  });

  // 모바일에서는 리스트 형태로 표시
  if (isMobile) {
    return (
      <Paper sx={{ height: '100%', overflow: 'auto' }}>
        <List>
          {sortedItems.map((item, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButton onClick={() => onItemClick(item)}>
                <ListItemIcon>
                  {item.type === 'folder' ? (
                    <FolderOpen color="primary" />
                  ) : (
                    <Box sx={{ fontSize: '1.5rem' }}>
                      {getFileIcon(item.name)}
                    </Box>
                  )}
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: item.type === 'folder' ? 'bold' : 'normal',
                        color: item.type === 'folder' ? 'primary.main' : 'inherit'
                      }}
                    >
                      {item.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      {item.type === 'file' && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(item.size)}
                          </Typography>
                          {item.lastModified && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              • {formatDistanceToNow(new Date(item.lastModified), { addSuffix: true })}
                            </Typography>
                          )}
                        </>
                      )}
                      <Box sx={{ mt: 0.5 }}>
                        {item.type === 'folder' ? (
                          <Chip 
                            label="폴더" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        ) : (
                          <Chip 
                            label={item.name.split('.').pop().toUpperCase()} 
                            size="small" 
                            color={getFileTypeColor(item.name)}
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
                
                {item.type === 'file' && (
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Tooltip title="파일 정보">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onGetInfo(item);
                          }}
                        >
                          <Info fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="다운로드">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDownload(item);
                          }}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>
    );
  }

  // 데스크톱에서는 테이블 형태로 표시
  return (
    <TableContainer component={Paper} sx={{ height: '100%' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '40px' }}></TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>이름</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>타입</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>크기</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '180px' }}>수정일</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>작업</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((item, index) => (
            <TableRow
              key={index}
              hover
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
              onClick={() => onItemClick(item)}
            >
              {/* 아이콘 */}
              <TableCell sx={{ padding: '8px' }}>
                {item.type === 'folder' ? (
                  <FolderOpen color="primary" />
                ) : (
                  <Box sx={{ fontSize: '1.2rem' }}>
                    {getFileIcon(item.name)}
                  </Box>
                )}
              </TableCell>
              
              {/* 파일/폴더 이름 */}
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: item.type === 'folder' ? 'bold' : 'normal',
                      color: item.type === 'folder' ? 'primary.main' : 'inherit'
                    }}
                  >
                    {item.name}
                  </Typography>
                </Box>
              </TableCell>
              
              {/* 타입 */}
              <TableCell>
                {item.type === 'folder' ? (
                  <Chip 
                    label="폴더" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                ) : (
                  <Chip 
                    label={item.name.split('.').pop().toUpperCase()} 
                    size="small" 
                    color={getFileTypeColor(item.name)}
                    variant="outlined"
                  />
                )}
              </TableCell>
              
              {/* 크기 */}
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {item.type === 'file' ? formatFileSize(item.size) : '-'}
                </Typography>
              </TableCell>
              
              {/* 수정일 */}
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {item.lastModified ? 
                    formatDistanceToNow(new Date(item.lastModified), { addSuffix: true }) : 
                    '-'
                  }
                </Typography>
              </TableCell>
              
              {/* 작업 버튼 */}
              <TableCell>
                {item.type === 'file' && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="파일 정보">
                      <IconButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onGetInfo(item);
                        }}
                      >
                        <Info fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="다운로드">
                      <IconButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDownload(item);
                        }}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {item.type === 'folder' && (
                  <Tooltip title="폴더 열기">
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemClick(item);
                      }}
                    >
                      <FolderOpen fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FileList;
